import {inject, injectable} from "inversify";
import {Match} from "@/modules/match/entity";
import {Stadium, StadiumLog, Terrace, TerraceLog} from "@/modules/stadium/entity";
import {BankCategory} from "@/modules/finances/enums";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";

@injectable()
// @see https://docs.google.com/spreadsheets/d/1qmof-UpyBExH-YmFuhE7y3G0sMdADqngC12eJfgcafo/edit#gid=1020838857
export default class SpectatorHandler {
  readonly powerFactor = 1.25;
  readonly feeShift = 50;
  readonly awayFactor = 0.2;
  readonly foundationFactor = 0.03;

  constructor(
    @inject(GateManager) private gateManager: GateManager,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {
  }

  public async handle(match: Match) {
    const stadium = await Stadium
      .createQueryBuilder('stadium')
      .innerJoinAndSelect('stadium.terraces', 'terrace')
      .innerJoin('stadium.team', 'team')
      .where('team.id = :id', {id: match.homeTeam.id})
      .getOneOrFail();

    const stadiumLog = new StadiumLog();
    stadiumLog.match = match;
    await stadiumLog.save();

    let income = 0;

    stadiumLog.terraceLogs = await stadium
      .terraces
      .mapAsync(async (terrace: Terrace) => {
        const seats = this.getSeats(stadium, terrace, this.getTotalPrestige(match, stadium));

        const terraceLog = new TerraceLog();
        terraceLog.stadiumLog = stadiumLog;
        terraceLog.seats = seats;
        terraceLog.capacity = terrace.seats;
        terraceLog.type = terrace.type;
        await terraceLog.save();

        income += seats * terrace.entranceFee;

        return terraceLog;
      });

    match.stadiumLog = stadiumLog;
    await match.save();

    if (match.homeTeam.user) {
      await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.add(
        income,
        {
          category: BankCategory.ENTRANCE_FEE,
          label: translator.t('entrance_fee'),
        }
      );
    }
  }

  private getSeats(stadium: Stadium, terrace: Terrace, prestige: number) {
    const terraceCount = stadium.terraces.filter((terrace: Terrace) => terrace.available).length;
    const factor = this.configRepository.get('stadium.spectators.terracesFactor')[terrace.type];
    const spectators = Math.round(factor * prestige * this.getPercent(terrace) / 100 / terraceCount);

    return Math.min(spectators, terrace.seats);
  }

  private getPercent(terrace: Terrace) {
    return -(terrace.entranceFee ** this.powerFactor - this.feeShift) + 100;
  }

  private getTotalPrestige(match: Match, stadium: Stadium) {
    const homePrestige = match.homeTeam.prestige * (1 + stadium.foundationLevel * this.foundationFactor);
    const awayPrestige = match.awayTeam.prestige * this.awayFactor;

    return homePrestige + awayPrestige;
  }
}
